import {
  FileOutlined,
  FormOutlined,
  InfoCircleOutlined,
  RollbackOutlined,
  SendOutlined,
  ShoppingCartOutlined,
  UserAddOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { AccordionItem, Link } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { EmailOpenIcon, KlaviyoIcon, RouteIcon } from '@mm/icons';
import { ClickIcon } from '@mm/icons/general/ClickIcon';
import { useViewActions } from '@mm/state/actions/viewActions';
import {
  ContactCustomerJourney,
  ContactCustomerJourneyMessage,
  FormFill,
} from '@mm/state/interfaces/contacts';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import { BotBuilderRoutes } from '../../../../../../../routes/bot-builder-routes';
import { TABLET_SMALL } from '../../../../../../constants/screenSize';
import { humanizedIntegrationNames } from '../../../../../beta/modules/Dashboard/ActivationBlock/use-activation-steps';
import { IntegrationIcon } from '../../../../../beta/modules/Integrations/IntegrationIcon/IntegrationIcon';
import styles from './CustomerJourney.module.css';

type Props = {
  customerJourney?: ContactCustomerJourney;
  formFills?: FormFill[];
  className?: string;
};
type ContactCustomerJourneyItem = ContactCustomerJourney[0];
const CustomerJourneyContent = (props: {
  customerJourney: ContactCustomerJourney;
  formFills: FormFill[];
}) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { hideSlideoutMenu } = useViewActions();
  const { customerJourney, formFills } = props;

  const customerJourneyRemappedTypes = customerJourney.map(item => {
    if (item.kind === 'integration_first_sync') {
      switch (item.structure.type) {
        case 'capi':
          return { ...item, structure: { type: 'facebook_ads_audience' } };
        case 'webhook':
          return { ...item, structure: { type: 'default' } };
        default:
          return item;
      }
    }
    return item;
  });

  if (!customerJourney.length) {
    return null;
  }
  const closeSlideout = () => {
    hideSlideoutMenu();
  };
  const emails = new Map(
    customerJourney
      .filter(item => item.kind === 'message')
      .map(item => [
        item.time,
        (item as ContactCustomerJourneyMessage).structure.subject,
      ]),
  );
  const firstUrlVisitedTimestamp = customerJourneyRemappedTypes.filter(
    item => item.kind === 'url_visited',
  )[0]?.time;
  const contentBuilder = (item: ContactCustomerJourneyItem) => {
    switch (item.kind) {
      case 'url_visited':
        const showVisitedAgain =
          item.structure.newSession && item.time !== firstUrlVisitedTimestamp;
        return (
          <>
            <div>
              {showVisitedAgain ? <RollbackOutlined /> : <FileOutlined />}
            </div>
            <div>
              {showVisitedAgain ? 'Visited website again' : 'Visited page'}
              <br />
              <Link
                capitalize={false}
                to={{
                  pathname: BotBuilderRoutes.contacts.getLink({ chatbotId }),
                  state: {
                    query: {
                      attributes: { urls_visited: [item.structure.url] },
                    },
                  },
                }}
                onLinkClick={() => closeSlideout()}
              >
                {item.structure.url}
              </Link>
            </div>
          </>
        );

      case 'read':
        return (
          <>
            <div>
              <EmailOpenIcon color="#595959" />
            </div>
            <div>
              Opened an email <strong>{item.structure.subject}</strong>
            </div>
          </>
        );
      case 'link_clicked':
        const emailSubject = emails.get(Number(item.structure.emailTimestamp));
        if (!emailSubject) return null;
        return (
          <>
            <div>
              <ClickIcon />
            </div>
            <div>
              Clicked on a link in the email <strong>{emailSubject}</strong>
            </div>
          </>
        );
      case 'message':
        return (
          <>
            <div>
              <SendOutlined />
            </div>
            <div>
              Email sent <strong>{item.structure.subject}</strong>
            </div>
          </>
        );
      case 'new_xray_contact':
        if (!item.structure.name) return null;
        return (
          <>
            <div>
              <UserAddOutlined />
            </div>
            <div>
              <strong>Meet your new X-Ray Lead</strong>
              <br />
              Website visitor <strong>{item.structure.name}</strong> was
              detected
            </div>
          </>
        );
      case 'integration_first_sync':
        return (
          <>
            <IntegrationIcon
              integrationType={item.structure.type}
              isActive
              iconProps={{ size: 16 }}
              className={styles.icon}
            />
            <div>
              Synced to{' '}
              {item.structure.type
                ? humanizedIntegrationNames[item.structure.type]
                : 'CRM'}
            </div>
          </>
        );

      case 'form_filled': {
        if (!item.structure.pageTitle) return null;

        // Note: Yevgen said we should match by timestamp
        const formFillByTimestamp = formFills.find(
          formFill => new Date(formFill.timestamp).getTime() === item.time,
        );

        const formFillEmail = formFillByTimestamp?.email;

        return (
          <>
            <div>
              <FormOutlined style={{ fontSize: '16px' }} />
            </div>
            <div>
              <div>
                Form filled: <strong>{item.structure.pageTitle}</strong>
              </div>
              {formFillEmail ? <div>{formFillEmail}</div> : null}
            </div>
          </>
        );
      }
      case 'klaviyo_email_received':
      case 'klaviyo_email_opened':
      case 'klaviyo_email_clicked': {
        const action =
          item.kind === 'klaviyo_email_received'
            ? 'received'
            : item.kind === 'klaviyo_email_opened'
            ? 'opened'
            : 'clicked';
        return (
          <>
            <div>
              <KlaviyoIcon size={16} color="black" />
            </div>
            <div>
              Klaviyo email {action}{' '}
              <strong>
                {item.structure.campaignName || item.structure.subject}
              </strong>
            </div>
          </>
        );
      }
      case 'klaviyo_placed_order':
        return (
          <>
            <div>
              <ShoppingCartOutlined style={{ fontSize: '16px' }} />
            </div>
            <div>
              Order placed. Total price:{' '}
              <strong>{`${item.structure.value} ${item.structure.currency}`}</strong>
            </div>
          </>
        );
      case 'add_to_cart':
        return (
          <>
            <div>
              <ShoppingCartOutlined style={{ fontSize: '16px' }} />
            </div>
            <div>
              Product{' '}
              <strong>{item.structure.sku || item.structure.title}</strong> is
              added to the cart. Quantity:{' '}
              <strong>{item.structure.quantity}</strong>. Total price:{' '}
              <strong>{`${item.structure.value} ${item.structure.currency}`}</strong>
            </div>
          </>
        );
      case 'initiate_checkout':
        return (
          <>
            <div>
              <WalletOutlined style={{ fontSize: '16px' }} />
            </div>
            <div>
              Navigated to checkout with total price{' '}
              <strong>{`${item.structure.value} ${item.structure.currency}`}</strong>
            </div>
          </>
        );
      default:
        return null;
    }
  };
  return (
    <>
      {customerJourneyRemappedTypes.map(item => {
        const content = contentBuilder(item);
        if (!content) return null;
        const date = moment(new Date(item.time));

        return (
          <div className={styles.dataRow} key={`${item.time} ${item.kind}`}>
            {content}
            <div>{date.format('M/D/YY')}</div>
            <div>{date.format('h:mm a')}</div>
          </div>
        );
      })}
    </>
  );
};

export const CustomerJourney = ({
  customerJourney,
  className,
  formFills = [],
}: Props) => {
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;
  if (!customerJourney?.length) {
    return null;
  }

  return (
    <div className={classNames(styles.wrapper, className)}>
      <AccordionItem
        icon={<RouteIcon className="contact-details__header-icon" />}
        count={customerJourney?.length || ''}
        title="Customer Journey"
        closeOnOutsideClick={false}
        className={styles.accordionItem}
        headingClassName={classNames(
          'contact-details__accordion-item-heading',
          'contact-details__accordion-item-heading-major',
        )}
        showArrow={!!isMobile}
        caretStyleIcon={!!isMobile}
        withBorder={false}
        canToggle={!!isMobile}
        openOnMount
      >
        <div className={styles.content}>
          <div className={styles.infoAlert}>
            <InfoCircleOutlined className={styles.infoIcon} />
            Tracking begins after your X-Ray Pixel has captured the lead.
          </div>
          <CustomerJourneyContent
            customerJourney={customerJourney}
            formFills={formFills}
          />
        </div>
      </AccordionItem>
    </div>
  );
};
